import { FC, useEffect, useState } from 'react';
import { Avatar, Divider, Typography } from 'antd';

import { AppIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useLatestRelease } from '@/hooks/useLatestRelease';
import manifest from '@/manifest.json';
import { EnrichedAnchorTag, FullHeightFlexColumn, MiddleDot, SmallTooltip } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { dayjs, formatDate } from '@/utilities';

import Metadata from './meta';

const About: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);
    const { latestRelease } = useLatestRelease();

    useEffect(() => {
        const loadingTimeout = window.setTimeout(() => {
            setLoading(false);
        }, 750);

        return () => window.clearTimeout(loadingTimeout);
    }, []);

    const { token } = useAntDesignTheme();

    return (
        <FullHeightFlexColumn>
            <Metadata />
            <main
                style={{
                    display: 'flex',
                    flex: '1 1 0%',
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'auto',
                    textAlign: 'center',
                    width: '100%',
                }}
            >
                <section
                    aria-label='About Countercyclical'
                    style={{
                        margin: 'auto',
                        padding: '1.5rem 0',
                        width: 'min(100%, 32rem)',
                    }}
                >
                    {loading ? (
                        <BaseSkeleton active round title paragraph={{ rows: 16 }} />
                    ) : (
                        <>
                            <Avatar
                                src={AppIcon}
                                shape='square'
                                size={64}
                                style={{ margin: '0 auto', backgroundColor: 'transparent' }}
                            />
                            <Typography.Title level={5} style={{ marginTop: '0.75rem' }}>
                                Countercyclical
                            </Typography.Title>
                            <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: '0.5rem' }}>
                                {latestRelease?.tag_name && latestRelease?.tag_name}
                                <MiddleDot horizontalMargin='0.5rem' />
                                Released{' '}
                                {latestRelease?.created_at && (
                                    <SmallTooltip
                                        placement='right'
                                        padding='0 0.5rem'
                                        title={
                                            latestRelease?.created_at &&
                                            dayjs(new Date(latestRelease?.created_at)).format('LLLL')
                                        }
                                    >
                                        {latestRelease?.created_at && formatDate(latestRelease?.created_at)}
                                    </SmallTooltip>
                                )}
                            </Typography.Paragraph>
                            <small style={{ marginTop: '0.75rem' }}>
                                <span style={{ color: token.colorTextTertiary }}>
                                    {manifest.address.businessName}
                                    <br />
                                    {manifest.address.line1}, {manifest.address.line2}
                                    <br />
                                    {manifest.address.city}, {manifest.address.state} {manifest.address.zipCode}
                                    <br />
                                    {manifest.address.country}
                                    <br />
                                    <div style={{ marginTop: '0.75rem' }}>
                                        Copyright &copy; 2022 - {new Date().getFullYear()}
                                        <br />
                                        All Rights Reserved
                                    </div>
                                </span>
                            </small>
                            <Divider />
                            <small>
                                <span style={{ color: token.colorTextTertiary }}>Logos Provided by</span>{' '}
                                <EnrichedAnchorTag label='Logo.dev' url='https://logo.dev' />
                            </small>
                        </>
                    )}
                </section>
            </main>
        </FullHeightFlexColumn>
    );
};

export default About;
